"use client";
import React, { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import Base from "@/templates/Base";
import Container from "@/components/Container";
import ServiceItem, { SkeletonServiceItem } from "@/components/ServiceItem";
import styles from "./styles.module.scss";
import { StoreItem } from "@/components/StoreItem";
import { Filters } from "@/components/Filters";
import { chips, filterDataInitial } from "@/utils/tsx/filters";
import Pagination from "@/components/shared/Pagination";
import type { FilterDataProps, StoreListItem } from "@/types/filters";
import { FacilidadeType } from "@/types/facilities";
import { orderStoresByGeo } from "@/utils/geoLocation";
import BannerCover from "@/components/BannerCover";
import Snackbar from "@/components/shared/Snackbar/snackbar";
import { type SnackbarProps } from "@/components/shared/Snackbar/snackbar.types";
import { useWindowSize } from "@/hooks/useWindowSize";
import { validateZipcode } from "@/utils/helpers";
import { getFreightData } from "@/services/config";

const CarouselFlex = dynamic(() => import("@/components/CarouselFlex"), {
  loading: () => (
    <div className={styles["skeleton-wrapper"]}>
      {Array.from({ length: 5 }).map((_, index) => (
        <SkeletonServiceItem key={index} />
      ))}
    </div>
  ),
  ssr: true
});

interface StoreListingProps {
  title: string;
  subtitle: string;
  banner: {
    url: string;
    alternativeText: string;
  }[];
  bannerMobile?: {
    url: string;
    alternativeText: string;
  };
  facilidades: FacilidadeType[];
  storeListData: StoreListItem[];
}

const INITIAL_PAGINATION = {
  total: 0,
  page: 1,
  pageSize: 12,
  pageCount: 1
};

const ACCEPTED_LOCALIZATION = "acceptedLocalization";

const StoreListing = ({
  title,
  subtitle,
  banner,
  bannerMobile,
  storeListData,
  facilidades
}: StoreListingProps) => {
  const [loadingFetcher, setLoadingFetcher] = useState(true);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [storeList, setStoreList] = useState<StoreListItem[]>([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [filterData, setFilterData] =
    useState<FilterDataProps>(filterDataInitial);
  const [cacheLastFilter, setCacheLastFilter] = useState<
    FilterDataProps | undefined
  >(undefined);
  const [snackbarData, setSnackbarData] = useState<SnackbarProps>({
    show: false,
    message: "",
    variant: undefined
  });

  useEffect(() => {
    if (storeListData.length > 0) {
      setLoadingFetcher(false);
    }
  }, [storeListData]);

  const handleCloseSnackbar = () => {
    setSnackbarData({ show: false, message: "", variant: undefined });
  };

  const listRef = useRef<HTMLDivElement>(null);

  const { mobile } = useWindowSize();

  const goToTop = () => {
    if (listRef.current) {
      if (mobile) {
        const topPosition = listRef.current.offsetTop + 500;
        window.scrollTo({ top: topPosition, behavior: "smooth" });
      } else {
        listRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }
  };

  const filterStores = async (filters: FilterDataProps) => {
    let filteredStores = [...storeListData];

    if (filters.zipcode && validateZipcode(filters.zipcode)) {
      try {
        const { data } = await getFreightData(filters.zipcode.replace("-", ""));
        const { geoCoordinates } = data;

        if (geoCoordinates && geoCoordinates[0] && geoCoordinates[1]) {
          const storesByGeo = await orderStoresByGeo({
            storeList: storeListData,
            latitude: geoCoordinates[1],
            longitude: geoCoordinates[0]
          });

          filteredStores = storesByGeo;
        }
      } catch (e) {
        filteredStores = [];
      }
    } else {
      if (filters.state) {
        filteredStores = filteredStores.filter(
          (store) => store.address.state === filters.state
        );
      }
      if (filters.city) {
        filteredStores = filteredStores.filter(
          (store) => store.address.city === filters.city
        );
      }
      if (filters.neighborhood) {
        filteredStores = filteredStores.filter(
          (store) => store.address.neighborhood === filters.neighborhood
        );
      }
      if (filters.facilidades.length > 0) {
        filteredStores = filteredStores.filter((store) =>
          filters.facilidades.every((filterFacility) =>
            store.facilidades.some((storeFacility) => {
              const facilitie = chips.find(
                (chip) => chip.title === filterFacility
              );

              return facilitie?.tags.includes(storeFacility.name);
            })
          )
        );
      }
    }

    if (sessionStorage.getItem("lat") && sessionStorage.getItem("long")) {
      return orderStoresByGeo({
        storeList: filteredStores,
        latitude: parseFloat(sessionStorage.getItem("lat") as string),
        longitude: parseFloat(sessionStorage.getItem("long") as string)
      });
    }

    return filteredStores;
  };

  const fetchStores = async (page: number, scrollToList?: boolean) => {
    const filters = filterData;

    // Verifica se o filtro é igual ao último filtro aplicado
    if (
      cacheLastFilter &&
      cacheLastFilter.city === filterData.city &&
      cacheLastFilter.state === filterData.state &&
      cacheLastFilter.neighborhood === filterData.neighborhood &&
      cacheLastFilter.zipcode === filterData.zipcode &&
      cacheLastFilter.facilidades === filterData.facilidades
    ) {
      return setCacheLastFilter(filterData);
    }

    setCacheLastFilter(filterData);

    setLoadingScreen(true);

    const lojas = await filterStores(filters);

    setStoreList(lojas as StoreListItem[]);
    setPagination({
      page,
      pageCount: Math.ceil(lojas.length / pagination.pageSize) || 1,
      pageSize: pagination.pageSize,
      total: lojas.length
    });

    // Tratamento para que o Scroll não aconteça no carregamento da página
    if (scrollToList) {
      goToTop();
    }

    setTimeout(() => {
      setLoadingScreen(false);
    }, 1000);
  };

  useEffect(() => {
    if (localStorage.getItem(ACCEPTED_LOCALIZATION) === "true") {
      handleGeolocation();
    } else {
      fetchStores(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStores(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData.facilidades, filterData.filterBy]);

  const handleConfirmFilters = (data: FilterDataProps) => {
    setFilterData(data);
    fetchStores(1, true);
  };

  const handleGeolocation = async () => {
    if (storeList.length === 0) {
      setLoadingFetcher(true);
    }

    if (!navigator.geolocation) {
      return console.log(
        "Sinto muito. Seu navegador nao suporta geolocalizacao."
      );
    }

    const error = ({ code }: any) => {
      if (code === 1) {
        fetchStores(1);
        setLoadingFetcher(false);

        setSnackbarData({
          show: true,
          message:
            "Não foi possível identificar a localização. Você precisa ativar os serviços para usar essa busca.",
          variant: "error"
        });
      }
    };

    const success = async ({ coords }: any) => {
      setLoadingScreen(true);

      localStorage.setItem(ACCEPTED_LOCALIZATION, "true");
      sessionStorage.setItem("lat", coords.latitude);
      sessionStorage.setItem("long", coords.longitude);

      const storesByGeo = await orderStoresByGeo({
        storeList: storeListData,
        latitude: coords.latitude,
        longitude: coords.longitude
      });

      setFilterData(filterDataInitial);
      setStoreList(storesByGeo);
      setPagination({
        page: 1,
        pageCount: Math.ceil(storesByGeo.length / pagination.pageSize) || 1,
        pageSize: pagination.pageSize,
        total: storesByGeo.length
      });
      setLoadingFetcher(false);

      setTimeout(() => {
        setLoadingScreen(false);
        if (mobile) {
          goToTop();
        }
      }, 1000);
    };

    if (localStorage.getItem(ACCEPTED_LOCALIZATION) !== "true") {
      setLoadingFetcher(true);
      setStoreList([]);
    }

    return navigator.geolocation.getCurrentPosition(success, error);
  };

  const paginate = (selected: number) => {
    setPagination({ ...pagination, page: selected });
    goToTop();
    setLoadingScreen(true);

    setTimeout(() => {
      setLoadingScreen(false);
    }, 500);
  };

  const showCardLoading =
    (storeList.length === 0 && loadingScreen) || loadingFetcher;

  const start = (pagination.page - 1) * pagination.pageSize;
  const paginatedStores = storeList.slice(start, start + pagination.pageSize);

  const showCardError =
    !showCardLoading && !loadingFetcher && paginatedStores.length === 0;

  return (
    <Base isLoading={loadingScreen}>
      <main>
        <BannerCover banner={banner} bannerMobile={bannerMobile} />
        <Container>
          <div className={styles.services}>
            <h1 className="heading-xs">{title}</h1>
            <p className="body-text-md">{subtitle}</p>
            <CarouselFlex
              showArrows
              className="carousel-facilities"
              classNameArrow="arrow btn btn-md btn-icon-rounded outlined"
            >
              {facilidades.map((item, index) => (
                <ServiceItem
                  key={index}
                  image={item.photo?.url || ""}
                  isPetAnjoService={item.petanjo}
                  title={item.name}
                  description={item.description || ""}
                  link={item.link || ""}
                />
              ))}
            </CarouselFlex>
          </div>

          <div ref={listRef}>
            <Filters
              storeListData={storeListData}
              filterData={filterData}
              onFilter={handleConfirmFilters}
              onChange={setFilterData}
              onFindLocation={handleGeolocation}
            />

            {storeList?.length > 0 && !(showCardError || showCardLoading) && (
              <>
                <div className={`${styles.listCard}`}>
                  {paginatedStores.map((item, index) => (
                    <StoreItem
                      key={`${item.slug}-${index}`}
                      item={item as StoreListItem}
                    />
                  ))}
                </div>
                <Pagination
                  pageCount={pagination.pageCount}
                  onPageChange={paginate}
                  forcePage={pagination.page - 1}
                  current={pagination.page}
                  key={`current-${pagination.page}`}
                  showLessItems={mobile}
                />
              </>
            )}

            {(showCardError || showCardLoading) && (
              <>
                <div className={styles.cardContainer}>
                  <div
                    key="card-loading"
                    className={`${styles.cardListEmpty} ${showCardLoading && styles.show}`}
                  >
                    <div className="avatar-illustration"></div>
                    <div className="card-list-content">
                      <h6 className="heading-xs">
                        Está quase! Os resultados para sua busca aparecerão
                        aqui.
                      </h6>
                    </div>
                  </div>
                  <div
                    key="card-error"
                    className={`${styles.cardListEmpty} ${showCardError && styles.show}`}
                  >
                    <div className="avatar-illustration--generic-error-icon"></div>
                    <div className="card-list-content">
                      <h6 className="heading-xs">
                        Desculpe, não encontramos resultados para esta busca
                      </h6>
                      <span className="subtitle-md">
                        Sugerimos que pesquise por localização ou altere os
                        filtros de busca
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Container>
      </main>
      <Snackbar
        theme="dark"
        {...snackbarData}
        onCloseButton={handleCloseSnackbar}
      />
    </Base>
  );
};

export default StoreListing;
